import axios from 'axios';
import { getEnabledModel, AIModelConfig } from '../config/aiModels';

// Maximum number of retries for API calls
const MAX_RETRIES = 5;
const RETRY_DELAY = 5000; // 5 seconds

/**
 * Helper function to implement retry logic for API calls
 */
const withRetry = async <T>(
  fn: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    
    // Check if error is rate limiting or server error
    if (axios.isAxiosError(error)) {
      const axiosError = error as any;
      if (axiosError.response?.status === 429 || axiosError.response?.status === 500) {
        console.log(`API call failed with status ${axiosError.response.status}. Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return withRetry(fn, retries - 1, delay * 2); // Exponential backoff
      }
    }
    
    throw error;
  }
};

/**
 * Generate content using the currently enabled AI model
 */
export const generateWithAI = async (
  systemPrompt: string,
  userPrompt: string
): Promise<string> => {
  const model = getEnabledModel();
  
  console.log('Selected AI model:', model.name);
  console.log('Provider:', model.provider);
  console.log('Model ID:', model.model);
  console.log('API Key Env Var:', model.apiKeyEnvVar);
  
  return withRetry(async () => {
    switch (model.provider) {
      case 'openai':
        console.log('Using OpenAI provider');
        return await generateWithOpenAI(model, systemPrompt, userPrompt);
      case 'deepseek':
        console.log('Using DeepSeek provider');
        return await generateWithDeepSeek(model, systemPrompt, userPrompt);
      case 'openrouter':
        console.log('Using OpenRouter provider');
        return await generateWithOpenRouter(model, systemPrompt, userPrompt);
      default:
        throw new Error(`Unsupported provider: ${model.provider}`);
    }
  });
};

/**
 * Generate content using OpenAI API
 */
const generateWithOpenAI = async (
  model: AIModelConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> => {
  const apiKey = process.env[model.apiKeyEnvVar];
  if (!apiKey) {
    throw new Error(`API key not found for ${model.name}. Please set ${model.apiKeyEnvVar} in your .env file.`);
  }
  
  const baseUrl = model.baseUrl || 'https://api.openai.com/v1';
  
  // Log for debugging
  console.log('Using OpenAI API with model:', model.model);
  console.log('API Key exists:', !!apiKey);
  console.log('Base URL:', baseUrl);
  
  const response = await axios.post(
    `${baseUrl}/chat/completions`,
    {
      model: model.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      temperature: model.temperature,
      max_tokens: model.maxTokens,
      top_p: 1
    },
    {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      timeout: 180000, // 180 seconds (3 minutes) timeout
    }
  );
  
  console.log('OpenAI API response received');
  
  if (!response.data.choices || response.data.choices.length === 0) {
    throw new Error('Invalid response from OpenAI API');
  }
  
  return response.data.choices[0].message.content;
};

/**
 * Generate content using DeepSeek API
 */
const generateWithDeepSeek = async (
  model: AIModelConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> => {
  const apiKey = process.env[model.apiKeyEnvVar];
  if (!apiKey) {
    throw new Error(`API key not found for ${model.name}. Please set ${model.apiKeyEnvVar} in your .env file.`);
  }
  
  const baseUrl = model.baseUrl || 'https://api.deepseek.com/v1/chat/completions';
  
  const response = await axios.post(
    baseUrl,
    {
      model: model.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      temperature: model.temperature,
      max_tokens: model.maxTokens
    },
    {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      timeout: 180000, // 180 seconds (3 minutes) timeout
    }
  );
  
  if (!response.data.choices || response.data.choices.length === 0) {
    throw new Error('Invalid response from DeepSeek API');
  }
  
  return response.data.choices[0].message.content;
};

/**
 * Generate content using OpenRouter API
 */
const generateWithOpenRouter = async (
  model: AIModelConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> => {
  const apiKey = process.env[model.apiKeyEnvVar];
  if (!apiKey) {
    throw new Error(`API key not found for ${model.name}. Please set ${model.apiKeyEnvVar} in your .env file.`);
  }
  
  const baseUrl = model.baseUrl || 'https://openrouter.ai/api/v1/chat/completions';
  
  const response = await axios.post(
    baseUrl,
    {
      model: model.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      temperature: model.temperature,
      max_tokens: model.maxTokens
    },
    {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'HTTP-Referer': 'https://sharkpanzer.com',
        'X-Title': 'SharkPanzer',
        'Content-Type': 'application/json',
      },
      timeout: 180000, // 180 seconds (3 minutes) timeout
    }
  );
  
  if (!response.data.choices || response.data.choices.length === 0) {
    throw new Error('Invalid response from OpenRouter API');
  }
  
  return response.data.choices[0].message.content;
};

/**
 * Parse JSON from AI response - SIMPLIFIED VERSION
 * This version prioritizes extracting numbered points directly from the response
 * rather than trying to parse complex JSON structures
 */
export const parseJsonFromAIResponse = (response: string): any => {
  // First, try to extract numbered points directly from the response
  const allNumberedPoints = response.match(/\d+\.\s*[^\n\d][^\n]+(\n[^\d\n][^\n]+)*/g) ||
                           response.match(/\d+\.\s*[^.]+/g) ||
                           response.match(/\d+\.\s*.+/g);
  
  if (allNumberedPoints && allNumberedPoints.length > 0) {
    // Filter out empty points
    const validPoints = allNumberedPoints.filter(point => {
      const content = point.replace(/^\d+\.\s*/, '').trim();
      return content !== '' && content !== '"' && content !== '",';
    });
    
    // Join all valid numbered points with newlines
    const fullCapsule = validPoints.join('\n\n');
    console.log(`Extracted ${validPoints.length} valid numbered points directly from response`);
    
    // Return a minimal valid object with the extracted content
    return {
      fullCapsule: fullCapsule,
      keyInsights: [],
      mindMap: { name: "Video Summary", children: [] },
      faqs: [],
      mcqs: [],
      flashCards: [],
      studyNotes: "",
      terminology: []
    };
  }
  
  // If no numbered points found, try to parse as JSON
  try {
    return JSON.parse(response);
  } catch (error) {
    // If JSON parsing fails, try to extract JSON from code blocks
    try {
      const jsonMatch = response.match(/```json\n([\s\S]*?)\n```/) ||
                        response.match(/```\n([\s\S]*?)\n```/) ||
                        response.match(/```([\s\S]*?)```/) ||
                        response.match(/\{[\s\S]*?\}/);
      
      if (jsonMatch) {
        const jsonContent = jsonMatch[1] || jsonMatch[0];
        
        // Try to extract fullCapsule directly using regex
        const fullCapsuleMatch = jsonContent.match(/"fullCapsule"\s*:\s*"([^"]+)"/);
        if (fullCapsuleMatch && fullCapsuleMatch[1]) {
          return {
            fullCapsule: fullCapsuleMatch[1],
            keyInsights: [],
            mindMap: { name: "Video Summary", children: [] },
            faqs: [],
            mcqs: [],
            flashCards: [],
            studyNotes: "",
            terminology: []
          };
        }
        
        // If direct extraction fails, try to parse the JSON
        try {
          return JSON.parse(jsonContent);
        } catch (jsonError) {
          // Fallback to using the raw response
          return {
            fullCapsule: response.replace(/```json|```/g, '').substring(0, 1000),
            keyInsights: [],
            mindMap: { name: "Video Summary", children: [] },
            faqs: [],
            mcqs: [],
            flashCards: [],
            studyNotes: "",
            terminology: []
          };
        }
      }
    } catch (extractError) {
      // Ignore extraction errors
    }
    
    // Final fallback
    return {
      fullCapsule: response.replace(/```json|```/g, '').substring(0, 1000),
      keyInsights: [],
      mindMap: { name: "Video Summary", children: [] },
      faqs: [],
      mcqs: [],
      flashCards: [],
      studyNotes: "",
      terminology: []
    };
  }
};

// No helper functions needed for the simplified implementation
//finally full capsule working 15 march 2025, 6pm, now let's enhance it and make it more effect in new branch tanmaydev1